import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { CreateCycleCountRequest, CycleCountDto, CycleCountLineInput, RecordCountsRequest, UpdateCycleCountRequest } from '@/types/adjustments';

const emptyLine: CycleCountLineInput = { materialId: '', locationId: null, batchId: null };

export default function CycleCountsPage() {
  const { t } = useTranslation('cycleCount');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();

  const { data: counts = [], isLoading } = useQuery({
    queryKey: ['cycle-counts'],
    queryFn: async () => (await apiClient.get<CycleCountDto[]>('/cycle-counts')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState('');
  const [lines, setLines] = useState<CycleCountLineInput[]>([{ ...emptyLine }]);

  const [selected, setSelected] = useState<CycleCountDto | null>(null);
  const [countedByLine, setCountedByLine] = useState<Record<string, number>>({});

  const resetForm = () => {
    setEditingId(null);
    setWarehouseId('');
    setLines([{ ...emptyLine }]);
  };

  // Only usable while Status is still "Draft" — mirrors CycleCountService.UpdateAsync's guard.
  const openEdit = (r: CycleCountDto) => {
    setEditingId(r.id);
    setWarehouseId(r.warehouseId);
    setLines(r.lines.map((l) => ({ materialId: l.materialId, locationId: l.locationId, batchId: l.batchId })));
    setOpen(true);
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId);
      if (editingId) {
        const payload: UpdateCycleCountRequest = { warehouseId, lines: cleanLines };
        return apiClient.put(`/cycle-counts/${editingId}`, payload);
      }
      const payload: CreateCycleCountRequest = { warehouseId, lines: cleanLines };
      return apiClient.post('/cycle-counts', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-counts'] });
      setOpen(false);
    },
  });

  const openCount = (count: CycleCountDto) => {
    setSelected(count);
    setCountedByLine(Object.fromEntries(count.lines.map((l) => [l.id, l.countedQty ?? l.systemQty])));
  };

  const recordMutation = useMutation({
    mutationFn: () => {
      const payload: RecordCountsRequest = { lines: Object.entries(countedByLine).map(([cycleCountLineId, countedQty]) => ({ cycleCountLineId, countedQty })) };
      return apiClient.post(`/cycle-counts/${selected!.id}/counts`, payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cycle-counts'] }),
  });

  const completeMutation = useMutation({
    mutationFn: () => apiClient.post(`/cycle-counts/${selected!.id}/complete`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cycle-counts'] });
      setSelected(null);
    },
  });

  const columns: DataTableColumn<CycleCountDto>[] = [
    { field: 'cycleCountNumber', headerName: t('table.number') },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'countDate', headerName: t('table.date'), render: (r) => formatDate(r.countDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.CYCLE_COUNT_EXECUTE}>
          <Stack direction="row" spacing={1}>
            {r.status === 'Draft' && (
              <Button size="small" onClick={() => openEdit(r)}>{t('edit')}</Button>
            )}
            {r.status !== 'Completed' && (
              <Button size="small" variant="outlined" onClick={() => openCount(r)}>{t('open')}</Button>
            )}
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.CYCLE_COUNT_EXECUTE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}>
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={counts} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="cycle-counts" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('edit') : t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
            {lines.map((line, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <SearchableSelect
                  label={t('form.material')} value={line.materialId} fullWidth size="small"
                  onChange={(materialId) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!warehouseId || lines.some((l) => !l.materialId) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>{selected.cycleCountNumber}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {selected.lines.map((line) => (
                  <Stack key={line.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Stack>
                      <span>{line.materialCode} — {line.materialName}</span>
                      <span style={{ fontSize: 12, opacity: 0.7 }}>{t('dialog.systemQty')}: {line.systemQty}</span>
                      {line.clientName ? (
                        <span style={{ fontSize: 12, opacity: 0.7 }}>{t('dialog.client')}: {line.clientName}</span>
                      ) : undefined}
                    </Stack>
                    <TextField
                      type="number" size="small" label={t('dialog.countedQty')} sx={{ minWidth: 120 }}
                      value={countedByLine[line.id] ?? 0}
                      onChange={(e) => setCountedByLine((prev) => ({ ...prev, [line.id]: Number(e.target.value) }))}
                    />
                  </Stack>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelected(null)}>{t('cancel')}</Button>
              <Button variant="outlined" disabled={recordMutation.isPending} onClick={() => recordMutation.mutate()}>{t('dialog.saveCounts')}</Button>
              <Button variant="contained" disabled={completeMutation.isPending} onClick={() => completeMutation.mutate()}>{t('dialog.complete')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
